import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import { SEO } from '../components/SEO';
import * as blogService from '../services/blogService';
import type { BlogPost } from '../services/blogService';

function formatDateLong(dateStr: string) {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();

  let suffix = 'th';
  if (day === 1 || day === 21 || day === 31) suffix = 'st';
  else if (day === 2 || day === 22) suffix = 'nd';
  else if (day === 3 || day === 23) suffix = 'rd';

  return `${month} ${day}${suffix}, ${year}`;
}

function formatDateShort(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateISO(dateStr: string) {
  return new Date(dateStr).toISOString();
}

function parseTags(post: BlogPost): string[] {
  try {
    return JSON.parse(post.tags || '[]');
  } catch {
    return [];
  }
}

/** Strip HTML tags to create a plain-text excerpt for meta descriptions */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export default function Blog(): React.ReactElement {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const activeTag = searchParams.get('tag') || null;

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [postsData, tagsData] = await Promise.all([
          blogService.getPublishedPosts(),
          blogService.getAllTags(),
        ]);
        setPosts(postsData || []);
        setAllTags(tagsData || []);

        // If URL has a slug param, find and select that post
        const safePostsData = postsData || [];
        if (slug) {
          const found = safePostsData.find(p => p.slug === slug);
          if (found) {
            setSelectedId(found.id);
            return;
          }
        }

        if (safePostsData.length > 0) {
          setSelectedId(safePostsData[0].id);
        }
      } catch (err) {
        console.error('Failed to load blog posts:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  // Filter posts by active tag
  const filteredPosts = activeTag
    ? posts.filter(p => parseTags(p).includes(activeTag))
    : posts;

  // When tag filter changes, select first filtered post
  useEffect(() => {
    if (filteredPosts.length > 0 && (!selectedId || !filteredPosts.find(p => p.id === selectedId))) {
      setSelectedId(filteredPosts[0].id);
    } else if (filteredPosts.length === 0) {
      setSelectedId(null);
    }
  }, [activeTag, filteredPosts.length]);

  const selectedPost = filteredPosts.find(p => p.id === selectedId) || null;

  function handleTagClick(tag: string) {
    if (activeTag === tag) {
      searchParams.delete('tag');
      setSearchParams(searchParams);
    } else {
      setSearchParams({ tag });
    }
  }

  function handlePostSelect(post: BlogPost) {
    setSelectedId(post.id);
    // Update URL to the individual post route (crawlable)
    navigate(`/blog/${post.slug}`, { replace: true });
  }

  // Auto-generate description from content if no excerpt
  const postDescription = selectedPost
    ? (selectedPost.excerpt || stripHtml(selectedPost.content).substring(0, 160))
    : 'Insights on requirements management, AI-powered project delivery, and building better software — from the team at Nivoon.';

  const postPath = selectedPost ? `/blog/${selectedPost.slug}` : '/blog';

  // Build structured data
  const structuredDataBlocks: object[] = [];

  if (selectedPost) {
    // Individual BlogPosting schema
    structuredDataBlocks.push({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.nivoon.com/blog/${selectedPost.slug}`,
      },
      headline: selectedPost.title,
      description: selectedPost.excerpt || stripHtml(selectedPost.content).substring(0, 160),
      datePublished: selectedPost.published_at,
      dateModified: selectedPost.updated_at,
      author: {
        '@type': 'Organization',
        name: 'Nivoon',
        url: 'https://www.nivoon.com',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Nivoon',
        url: 'https://www.nivoon.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.nivoon.com/logo1.png',
        },
      },
      ...(selectedPost.featured_image_url && {
        image: selectedPost.featured_image_url,
      }),
      ...(parseTags(selectedPost).length > 0 && {
        keywords: parseTags(selectedPost).join(', '),
      }),
    });

    // Breadcrumb schema
    structuredDataBlocks.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://www.nivoon.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://www.nivoon.com/blog',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: selectedPost.title,
          item: `https://www.nivoon.com/blog/${selectedPost.slug}`,
        },
      ],
    });
  } else {
    // Blog index schema with ItemList
    structuredDataBlocks.push({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Nivoon Blog',
      description: 'Insights on requirements management, AI-powered project delivery, and building better software — from the team at Nivoon.',
      url: 'https://www.nivoon.com/blog',
      publisher: {
        '@type': 'Organization',
        name: 'Nivoon',
        url: 'https://www.nivoon.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.nivoon.com/logo1.png',
        },
      },
    });

    if (posts.length > 0) {
      structuredDataBlocks.push({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: posts.slice(0, 20).map((post, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          url: `https://www.nivoon.com/blog/${post.slug}`,
          name: post.title,
        })),
      });
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={selectedPost ? `${selectedPost.title} | Blog` : 'Blog'}
        description={postDescription}
        path={postPath}
        ogType={selectedPost ? 'article' : 'website'}
      >
        {/* Article-specific OG tags */}
        {selectedPost && (
          <>
            {selectedPost.featured_image_url && (
              <meta property="og:image" content={selectedPost.featured_image_url} />
            )}
            {selectedPost.published_at && (
              <meta property="article:published_time" content={formatDateISO(selectedPost.published_at)} />
            )}
            <meta property="article:modified_time" content={formatDateISO(selectedPost.updated_at)} />
            <meta property="article:author" content="Nivoon" />
            <meta property="article:section" content="Technology" />
            {parseTags(selectedPost).map(tag => (
              <meta key={tag} property="article:tag" content={tag} />
            ))}
          </>
        )}
        {/* Structured data */}
        {structuredDataBlocks.map((block, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(block)}
          </script>
        ))}
      </SEO>

      <PublicNav activePage="blog" />

      {/* Breadcrumb nav (visible + semantic) */}
      {selectedPost && (
        <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <ol className="flex items-center text-sm text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-indigo-600 transition-colors" itemProp="item">
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="mx-2 text-gray-300">/</li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/blog" className="hover:text-indigo-600 transition-colors" itemProp="item">
                  <span itemProp="name">Blog</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li className="mx-2 text-gray-300">/</li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-gray-900 font-medium" itemProp="name">{selectedPost.title}</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </div>
        </nav>
      )}

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-indigo-950 to-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-indigo-400 font-semibold text-sm uppercase tracking-widest mb-3">Nivoon Blog</p>
          <h1 className="text-3xl md:text-5xl font-bold text-white blog-heading-font">
            Insights & Updates
          </h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            Thoughts on requirements management, AI in project delivery, and building software that matters.
          </p>
        </div>
      </section>

      {/* Tag Filters */}
      {allTags.length > 0 && (
        <section className="border-b border-gray-200 bg-gray-50" aria-label="Filter posts by topic">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-500 mr-2">Filter by topic:</span>
              <button
                onClick={() => {
                  searchParams.delete('tag');
                  setSearchParams(searchParams);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  !activeTag
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 border border-gray-300 hover:border-indigo-400 hover:text-indigo-600'
                }`}
              >
                All Posts
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeTag === tag
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-600 border border-gray-300 hover:border-indigo-400 hover:text-indigo-600'
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-500">Loading posts...</span>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {activeTag ? `No posts tagged #${activeTag}` : 'No blog posts yet'}
              </h3>
              <p className="text-gray-500">
                {activeTag ? 'Try a different tag or view all posts.' : 'Check back soon for new content.'}
              </p>
              {activeTag && (
                <button
                  onClick={() => {
                    searchParams.delete('tag');
                    setSearchParams(searchParams);
                  }}
                  className="mt-4 px-4 py-2 text-sm font-medium text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                >
                  View All Posts
                </button>
              )}
            </div>
          ) : (
            <div className="flex gap-10">
              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {selectedPost ? (
                  <article itemScope itemType="https://schema.org/BlogPosting">
                    <meta itemProp="url" content={`https://www.nivoon.com/blog/${selectedPost.slug}`} />

                    {/* Featured Image */}
                    {selectedPost.featured_image_url && (
                      <div className="mb-8 -mx-2 md:-mx-4">
                        <img
                          src={selectedPost.featured_image_url}
                          alt={selectedPost.title}
                          className="w-full rounded-xl shadow-lg object-cover max-h-[480px]"
                          itemProp="image"
                        />
                      </div>
                    )}

                    <header className="mb-8">
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight blog-heading-font" itemProp="headline">
                        {selectedPost.title}
                      </h2>
                      <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                        {selectedPost.published_at && (
                          <time dateTime={formatDateISO(selectedPost.published_at)} itemProp="datePublished">
                            {formatDateLong(selectedPost.published_at)}
                          </time>
                        )}
                        <span itemProp="author" itemScope itemType="https://schema.org/Organization">
                          by <span itemProp="name">{selectedPost.created_by_name}</span>
                        </span>
                      </div>
                      {selectedPost.excerpt && (
                        <>
                          <meta itemProp="description" content={selectedPost.excerpt} />
                          <p className="mt-4 text-lg text-gray-500 italic leading-relaxed border-l-4 border-indigo-200 pl-4">
                            {selectedPost.excerpt}
                          </p>
                        </>
                      )}
                    </header>
                    <div
                      className="blog-content prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:shadow-md prose-blockquote:border-l-4 prose-blockquote:border-indigo-500 prose-blockquote:bg-indigo-50 prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:px-6 prose-blockquote:not-italic prose-blockquote:text-gray-700 prose-blockquote:font-medium prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-5 prose-h2:pb-3 prose-h2:border-b prose-h2:border-gray-300 prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-3 prose-h3:font-bold prose-h3:text-gray-800 prose-hr:border-0 prose-hr:h-[2px] prose-hr:bg-gradient-to-r prose-hr:from-gray-200 prose-hr:via-gray-400 prose-hr:to-gray-200 prose-hr:my-14 prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-indigo-600 prose-code:before:content-none prose-code:after:content-none prose-pre:bg-gray-900 prose-pre:rounded-xl prose-pre:shadow-inner prose-li:text-gray-600"
                      dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                      itemProp="articleBody"
                    />
                    {/* Tags at bottom */}
                    {parseTags(selectedPost).length > 0 && (
                      <footer className="mt-10 pt-6 border-t border-gray-200">
                        <div className="flex flex-wrap gap-2" role="list" aria-label="Post tags">
                          {parseTags(selectedPost).map(tag => (
                            <button
                              key={tag}
                              role="listitem"
                              onClick={() => handleTagClick(tag)}
                              className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                            >
                              #{tag}
                            </button>
                          ))}
                        </div>
                      </footer>
                    )}
                  </article>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Select a post from the sidebar to read</p>
                  </div>
                )}
              </div>

              {/* Right Sidebar - Post List (uses <a> tags for crawlable internal links) */}
              <aside className="hidden lg:block w-80 shrink-0" aria-label="Blog post navigation">
                <nav className="sticky top-8">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                    {activeTag ? `#${activeTag}` : 'Recent Posts'}
                  </h3>
                  <div className="space-y-1">
                    {filteredPosts.map(post => (
                      <a
                        key={post.id}
                        href={`/blog/${post.slug}`}
                        onClick={e => {
                          e.preventDefault();
                          handlePostSelect(post);
                        }}
                        className={`block px-3 py-3 rounded-lg transition-colors ${
                          selectedId === post.id
                            ? 'bg-indigo-50 border-l-4 border-indigo-600'
                            : 'hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                      >
                        <span className={`text-sm font-medium line-clamp-2 block ${
                          selectedId === post.id ? 'text-indigo-700' : 'text-gray-800'
                        }`}>
                          {post.title}
                        </span>
                        {post.published_at && (
                          <time dateTime={formatDateISO(post.published_at)} className="text-xs text-gray-400 mt-1 block">
                            {formatDateShort(post.published_at)}
                          </time>
                        )}
                      </a>
                    ))}
                  </div>
                </nav>
              </aside>
            </div>
          )}
        </div>
      </section>

      {/* Mobile Post Selector (shown only on mobile when posts exist) */}
      {!loading && filteredPosts.length > 1 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <label htmlFor="mobile-post-select" className="sr-only">Select a blog post</label>
            <select
              id="mobile-post-select"
              value={selectedId || ''}
              onChange={e => {
                const post = filteredPosts.find(p => p.id === e.target.value);
                if (post) handlePostSelect(post);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900"
            >
              {filteredPosts.map(post => (
                <option key={post.id} value={post.id}>
                  {post.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to streamline your requirements?</h2>
          <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
            Start your free trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-3.5 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors shadow-lg text-lg"
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate('/features')}
              className="px-8 py-3.5 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors text-lg"
            >
              Explore Features
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="/features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="/about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="/careers" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="/investor-relations" className="hover:text-white transition-colors">Investor Relations</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="/legal" className="hover:text-white transition-colors">Privacy & Terms</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Get Started</h3>
              <ul className="space-y-2">
                <li><a href="/register" className="hover:text-white transition-colors">Sign Up Free</a></li>
                <li><a href="/login" className="hover:text-white transition-colors">Sign In</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Nivoon. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
